// pages/BigBoard.js
import React, {
  useState, useEffect, useContext, useRef
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Paper,
  Grid,
  Checkbox,
  IconButton,
  Button,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListItemText,
  Slider,
  Chip,
  Switch,
  ToggleButton,
  Divider,
  ToggleButtonGroup,
  Card,
  CardContent
} from '@mui/material';
import {
  Delete as DeleteIcon,
  VisibilityOff,
  School,
  Person,
  Refresh as RefreshIcon,
  Cancel as CancelIcon,
  RemoveRedEye as MonitorIcon,
  Star as StarIcon,
  Assignment as InformeIcon,
  Search as ScoutIcon,
  SportsBasketball as CompetitionIcon,
  TrendingUp,
  TrendingFlat,
  TrendingDown,
  Healing as HealingIcon,
  Assignment as AssignmentIcon,
  Lightbulb as LightbulbIcon,
  Description as DescriptionIcon,
  VideoLibrary as VideoLibraryIcon,
  Straighten as StraightenIcon,
  FolderOpen as FolderOpenIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { useDrag, useDrop, DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import api from '../api/axiosConfig';
import { getMediaBaseUrl } from '../utils/mediaUrl';
import { BigBoardContext } from '../BigBoardContext';
import FlagDisplay from '../components/FlagDisplay';
import { usePositions, DB_TO_TRANSLATION_KEY } from '../utils/positions';

/* -------------------------------------------------- */
/*      MAPPINGS, CONSTANTES, OUTILS                  */
/* -------------------------------------------------- */
const isVisible = (player, threshold) => player.classement <= threshold;

/* Helper components from PlayerResults */
const FormaIndicator = ({ forma }) => {
  if (!forma) return null;

  switch (forma?.toLowerCase()) {
    case 'high':
      return <TrendingUp sx={{ color: '#4caf50', fontSize: 20 }} />;
    case 'stable':
      return <TrendingFlat sx={{ color: '#ff9800', fontSize: 20 }} />;
    case 'low':
      return <TrendingDown sx={{ color: '#f44336', fontSize: 20 }} />;
    default:
      return null;
  }
};

const calculateDecimalAge = (anneeNaissance) => {
  if (!anneeNaissance) return '';
  const currentYear = new Date().getFullYear();
  const age = currentYear - anneeNaissance;
  return age;
};

const VerdictIndicator = ({ player }) => {
  if (!player.scout_verdicts || player.scout_verdicts.length === 0) return null;

  // Group verdicts by type
  const verdictGroups = player.scout_verdicts.reduce((acc, verdict) => {
    if (!acc[verdict.verdict]) {
      acc[verdict.verdict] = [];
    }
    acc[verdict.verdict].push(verdict);
    return acc;
  }, {});

  const verdictConfig = {
    'VOLVER A VER': { emoji: '🔄', color: '#2196f3' },
    'DESCARTADO': { emoji: '❌', color: '#f44336' },
    'MONITORIZAR': { emoji: '👁️', color: '#ff9800' },
    'PRIORITARIO': { emoji: '⭐', color: '#4caf50' },
    'DUDAS': { emoji: '❓', color: '#9c27b0' }
  };

  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      {Object.entries(verdictGroups).map(([verdictType, verdicts]) => {
        const config = verdictConfig[verdictType];
        if (!config) return null;

        const count = verdicts.length;

        return (
          <Box key={verdictType} sx={{ position: 'relative', display: 'inline-block' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                '&:hover': {
                  transform: 'scale(1.1)',
                  transition: 'transform 0.2s'
                }
              }}
            >
              {config.emoji}
            </Box>
            {count > 1 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: -6,
                  right: -6,
                  backgroundColor: config.color,
                  color: '#fff',
                  borderRadius: '50%',
                  width: 12,
                  height: 12,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 8,
                  fontWeight: 'bold',
                  border: '1px solid #fff',
                }}
              >
                {count}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

const CountBadge = ({ count }) => {
  const backgroundColor = count <= 1 ? '#f44336' : '#4caf50';

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        backgroundColor: backgroundColor,
        color: '#ffffff',
        width: '28px',
        height: '28px',
        fontSize: '12px',
        fontWeight: 'bold',
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
        border: '2px solid #fff',
      }}
    >
      {count}
    </Box>
  );
};

const badgeStyle = (bgColor) => ({
  position: 'relative',
  width: 24,
  height: 24,
  borderRadius: '50%',
  backgroundColor: bgColor,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  fontSize: '14px',
  marginLeft: '-4px',
  border: '1px solid #fff',
});

/* -------------------------------------------------- */
/*                 COMPOSANT PRINCIPAL                */
/* -------------------------------------------------- */
const BigBoard = () => {
  const { t } = useTranslation();

  /* ---------- Contexte global */
  const { bigBoardPlayers, setBigBoardPlayers } = useContext(BigBoardContext);

  /* ---------- Données & états locaux */
  const [players, setPlayers] = useState([]);

  const [page, setPage] = useState(0);
  const rowsPerPage = 50;

  /* Filtres */
  const [nameFilter, setNameFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [positionFilter, setPositionFilter] = useState([]);
  const [heightFilter, setHeightFilter] = useState([100, 230]);
  const [ageFilter, setAgeFilter] = useState([8, 42]);
  const [leagueFilter, setLeagueFilter] = useState([]);
  const [uniqueLeagues, setUniqueLeagues] = useState([]);
  const [playerTypeFilter, setPlayerTypeFilter] = useState(0);
  const [informeFilter, setInformeFilter] = useState(null);
  const [scoutFilter, setScoutFilter] = useState(null);
  const [verdictFilter, setVerdictFilter] = useState([]);
  const [competitionFilter, setCompetitionFilter] = useState(null);

  const [groups, setGroups] = useState([]);
  const [groupFilter, setGroupFilter] = useState([]);
  const [groupColors, setGroupColors] = useState({});

  /* Visibilité : top N visibles (5 par défaut) */
  const [visibleThreshold, setVisibleThreshold] = useState(5);

  /* Tags / nom du BigBoard */
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);

  const [selectedRankingNameOption, setSelectedRankingNameOption] = useState('');
  const [bigBoardName, setBigBoardName] = useState('');

  /* Player type (Pro / Cantera) dépend du rôle utilisateur */
  const [playerType, setPlayerType] = useState('Pro');
  const userData = JSON.parse(localStorage.getItem('user'));
  const userRole = userData?.role?.toLowerCase() || '';

  /* ---------- Environnement / hooks externes */
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const section = process.env.REACT_APP_SECTION;
  const mediaUrl = getMediaBaseUrl();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  /* -------------------------------------------------- */
  /*   Ajuster playerType en fonction du rôle           */
  /* -------------------------------------------------- */
  useEffect(() => {
    if (userRole === 'super admin') return;
    setPlayerType(userRole === 'admin pro' ? 'Pro' : 'Cantera');
  }, [userRole]);

  /* -------------------------------------------------- */
  /*   Récupération joueurs, groupes, tags              */
  /* -------------------------------------------------- */
  useEffect(() => {
    const fetchAll = async () => {
      try {
        /* Joueurs */
        const { data } = await api.get('/api/players');
        const allPlayers = data.players;

        /* Ligues uniques (propres) */
        const leagues = Array.from(
          new Set(
            allPlayers
              .map((p) => p.league_1)
              .filter(
                (l) =>
                  l !== null &&
                  l !== undefined &&
                  typeof l === 'string' &&
                  l.trim() !== '' &&
                  l.trim() !== 'null'
              )
          )
        ).sort();
        setUniqueLeagues(leagues);

        /* Exclure ceux déjà dans le BigBoard */
        const idsInBoard = bigBoardPlayers.map((p) => p.id);
        const available = allPlayers.filter((p) => !idsInBoard.includes(p.id));
        setPlayers(available);

        /* Groupes */
        const groupsRes = await api.get('/api/groups');
        setGroups(groupsRes.data);

        // Créer un mapping des couleurs des groupes
        const colors = {};
        groupsRes.data.forEach(group => {
          colors[group.libelle] = group.code_couleur;
        });
        setGroupColors(colors);

        /* Tags */
        const tagsRes = await api.get('/api/tags');
        setTags(tagsRes.data);
      } catch (err) {
        console.error('Erreur lors du chargement des données', err);
      }
    };
    fetchAll();
  }, [backendUrl, section, bigBoardPlayers]);

  /* -------------------------------------------------- */
  /*       Helpers filtrage et pagination               */
  /* -------------------------------------------------- */
  const calculateAge = (anneeNaissance) =>
    new Date().getFullYear() - anneeNaissance;

  // Hook positions pour la traduction
  const { positionsWithKeys } = usePositions(t);

  const verdictOptions = [
    { value: 'DESCARTADO', label: t('bigBoardFilters.verdictOptions.discarded'), icon: <CancelIcon fontSize="small" />, color: '#f44336' },
    { value: 'VOLVER A VER', label: t('bigBoardFilters.verdictOptions.watchAgain'), icon: <RefreshIcon fontSize="small" />, color: '#2196f3' },
    { value: 'MONITORIZAR', label: t('bigBoardFilters.verdictOptions.monitor'), icon: <MonitorIcon fontSize="small" />, color: '#ff9800' },
    { value: 'PRIORITARIO', label: t('bigBoardFilters.verdictOptions.priority'), icon: <StarIcon fontSize="small" />, color: '#4caf50' }
  ];

  const handleInformeClick = (color) => {
    if (informeFilter === color) {
      setInformeFilter(null);
    } else {
      setInformeFilter(color);
    }
  };

  const handleScoutClick = (color) => {
    if (scoutFilter === color) {
      setScoutFilter(null);
    } else {
      setScoutFilter(color);
    }
  };

  const getContrastColor = (hexcolor) => {
    if (!hexcolor) return '#000000';
    const r = parseInt(hexcolor.slice(1, 3), 16);
    const g = parseInt(hexcolor.slice(3, 5), 16);
    const b = parseInt(hexcolor.slice(5, 7), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#000000' : '#FFFFFF';
  };

  const filteredPlayers = players.filter((player) => {
    /* 1) Type Pro / Cantera - Filtre par playerTypeFilter */
    if (playerTypeFilter === 1) {
      if (!player.type || player.type.toLowerCase() !== 'cantera') return false;
    } else if (playerTypeFilter === 2) {
      if (!player.type || player.type.toLowerCase() !== 'pro') return false;
    } else {
      // Si playerTypeFilter === 0, on utilise playerType par défaut
      if (!player.type || player.type.toLowerCase() !== playerType.toLowerCase())
        return false;
    }

    /* 2) Recherche nom/équipe */
    const nameSearch = nameFilter.toLowerCase();
    const matchesName =
      player.nom.toLowerCase().includes(nameSearch) ||
      (player.team && player.team.toLowerCase().includes(nameSearch));
    if (!matchesName) return false;

    /* 2b) Filtre par année de naissance */
    if (yearFilter && !player.annee_naissance.toString().includes(yearFilter))
      return false;

    /* 2c) Filtre informe */
    if (informeFilter) {
      if (informeFilter === 'zero') {
        // 0 informes: players with exactly 0 informe
        const count = player.nb_informe || 0;
        if (count !== 0) return false;
      } else if (informeFilter === 'red') {
        // 0-1 informes: players with 0 or 1 informe
        const count = player.nb_informe || 0;
        if (count > 1) return false;
      } else if (informeFilter === 'green') {
        // 1+ informes: players with 2 or more informes
        const count = player.nb_informe || 0;
        if (count <= 1) return false;
      }
    }

    /* 2d) Filtre scout */
    if (scoutFilter) {
      if (scoutFilter === 'red') {
        // 0-1 scouts: players with 0 or 1 scout
        const count = player.nb_scout || 0;
        if (count > 1) return false;
      } else if (scoutFilter === 'green') {
        // 1+ scouts: players with 2 or more scouts
        const count = player.nb_scout || 0;
        if (count <= 1) return false;
      }
    }

    /* 2e) Filtre verdict */
    if (verdictFilter.length > 0) {
      // Check if player has any of the selected verdicts
      if (!player.scout_verdicts || player.scout_verdicts.length === 0) {
        return false;
      }

      // Check if any of the player's verdicts match the selected filters
      const playerVerdicts = player.scout_verdicts.map(v => v.verdict);
      const hasMatchingVerdict = verdictFilter.some(v => playerVerdicts.includes(v));

      if (!hasMatchingVerdict) return false;
    }

    /* 2f) Filtre competition */
    if (competitionFilter === 'with' && !player.competition) return false;
    if (competitionFilter === 'without' && player.competition) return false;

    /* 3) Position - compare les clés de position (pointGuard, shootingGuard, etc.) */
    if (
      positionFilter.length &&
      !positionFilter.some((posKey) => {
        const playerPositionKey = DB_TO_TRANSLATION_KEY[player.poste] || player.poste;
        return playerPositionKey === posKey;
      })
    )
      return false;

    /* 4) Ligue */
    if (leagueFilter.length && !leagueFilter.includes(player.league_1))
      return false;

    /* 5) Groupes */
    if (groupFilter.length) {
      if (groupFilter.includes('Sin Grupos')) {
        if (!player.groupes || player.groupes.length === 0) {
          // Le joueur n'a pas de groupe
        } else if (!groupFilter.some(g => g !== 'Sin Grupos')) {
          return false;
        }
      } else {
        const plGroupNames = (player.groupes || []).map((g) => g.libelle);
        const overlap = groupFilter.some((name) => plGroupNames.includes(name));
        if (!overlap) return false;
      }
    }

    /* 6) Taille */
    if (player.taille < heightFilter[0] || player.taille > heightFilter[1])
      return false;

    /* 7) Âge */
    const age = calculateAge(player.annee_naissance);
    if (age < ageFilter[0] || age > ageFilter[1]) return false;

    return true;
  });

  const getCurrentPagePlayers = () =>
    filteredPlayers.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );

  /* -------------------------------------------------- */
  /*               Drag & Drop – colonne gauche          */
  /* -------------------------------------------------- */
  const PlayerRow = ({ player }) => {
    const theme = useTheme();
    // Debug: Check nationalite field specifically
    if (!player.nationalite || player.nationalite === 'null') {
      console.log(`Player ${player.nom} has no nationalite:`, player.nationalite);
    }
    const [{ isDragging }, dragRef] = useDrag({
      type: 'player',
      item: player,
      collect: (monitor) => ({ isDragging: monitor.isDragging() })
    });

    const isInBigBoard = bigBoardPlayers.some((p) => p.id === player.id);

    const handleCheckbox = (e) => {
      if (e.target.checked) movePlayerToBigBoard(player);
      else removePlayerFromBigBoard(player);
    };

    return (
      <TableRow
        ref={dragRef}
        key={player.id}
        hover
        sx={{
          opacity: isDragging ? 0.5 : 1,
          cursor: 'move'
        }}
      >
        <TableCell sx={{ textAlign: 'center', padding: '4px' }}>
          <Checkbox checked={isInBigBoard} onChange={handleCheckbox} />
        </TableCell>
        <TableCell sx={{ textAlign: 'left', padding: '4px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FlagDisplay nationality={player.nationalite} />
            <Avatar
              src={`${mediaUrl}/photos/${player.id}.png`}
              alt={player.nom}
              sx={{ width: 36, height: 36 }}
            />
            <VerdictIndicator player={player} />
          </Box>
        </TableCell>
        <TableCell sx={{ textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', padding: '4px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography sx={{ fontSize: '14px' }}>
              {player.nom}
            </Typography>
            {player.is_in_injury === 1 && (
              <HealingIcon sx={{ color: 'red', fontSize: 16 }} />
            )}
          </Box>
        </TableCell>
        <TableCell sx={{ textAlign: 'left', padding: '4px' }}>
          <FormaIndicator forma={player.forme_indicator} />
        </TableCell>
        <TableCell sx={{ textAlign: 'left', fontSize: '13px', padding: '4px' }}>
          {player.poste}
        </TableCell>
        <TableCell sx={{ textAlign: 'left', fontSize: '13px', padding: '4px' }}>
          {player.annee_naissance}
          {player.annee_naissance && ` (${calculateDecimalAge(player.annee_naissance)})`}
        </TableCell>
        <TableCell sx={{ textAlign: 'left', fontSize: '13px', padding: '4px' }}>
          {player.taille}cm
        </TableCell>
        <TableCell sx={{ textAlign: 'center', fontSize: '13px', padding: '4px' }}>
          <Box>
            {player?.nationalite && player.nationalite !== 'null' ? (
              <>
                <div>{player.nationalite}</div>
                {player?.passport && player.passport !== 'null' && (
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                    {player.passport}
                  </div>
                )}
              </>
            ) : (
              <div>-</div>
            )}
          </Box>
        </TableCell>
        <TableCell sx={{ textAlign: 'center', fontSize: '13px', padding: '4px' }}>
          <Box>
            <div>{player.team}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>{player.league_1}</div>
          </Box>
        </TableCell>
        <TableCell sx={{ textAlign: 'left', padding: '4px' }}>
          {player.groupes && player.groupes.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {player.groupes.slice(0, 2).map((group, idx) => (
                <Chip
                  key={idx}
                  label={group.libelle}
                  size="small"
                  sx={{
                    backgroundColor: group.code_couleur || '#grey',
                    color: getContrastColor(group.code_couleur),
                    fontSize: '11px',
                    height: '20px'
                  }}
                />
              ))}
              {player.groupes.length > 2 && (
                <Chip
                  label={`+${player.groupes.length - 2}`}
                  size="small"
                  sx={{ fontSize: '11px', height: '20px' }}
                />
              )}
            </Box>
          )}
        </TableCell>
        <TableCell sx={{ textAlign: 'center', padding: '4px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
            <CountBadge count={player.nb_informe || 0} />
            <CountBadge count={player.nb_scout || 0} />
          </Box>
        </TableCell>
        <TableCell sx={{ textAlign: 'left', padding: '4px', paddingLeft: '8px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.25 }}>
            {player.is_in_big_board === 1 && (
              <Box sx={badgeStyle('#1976d2')}>
                <StarIcon fontSize="inherit" />
              </Box>
            )}
            {player.is_in_follow_list === 1 && (
              <Box sx={badgeStyle('#dc004e')}>
                <VisibilityIcon fontSize="inherit" />
              </Box>
            )}
            {player.is_in_informe === 1 && (
              <Box sx={badgeStyle('#4caf50')}>
                <AssignmentIcon fontSize="inherit" />
              </Box>
            )}
            {player.is_in_intel === 1 && (
              <Box sx={badgeStyle('#ff9800')}>
                <LightbulbIcon fontSize="inherit" />
              </Box>
            )}
            {player.is_in_fiche_technique === 1 && (
              <Box sx={badgeStyle('#2196f3')}>
                <DescriptionIcon fontSize="inherit" />
              </Box>
            )}
            {player.is_in_video === 1 && (
              <Box sx={badgeStyle('#9c27b0')}>
                <VideoLibraryIcon fontSize="inherit" />
              </Box>
            )}
            {player.has_medidas === 1 && (
              <Box sx={badgeStyle('#00bcd4')}>
                <StraightenIcon fontSize="inherit" />
              </Box>
            )}
            {player.has_documents === 1 && (
              <Box sx={badgeStyle('#795548')}>
                <FolderOpenIcon fontSize="inherit" />
              </Box>
            )}
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  /* -------------------------------------------------- */
  /*               Drag & Drop – BigBoard               */
  /* -------------------------------------------------- */
  const movePlayerToBigBoard = (player) => {
    if (bigBoardPlayers.some((p) => p.id === player.id)) return;

    /* Décale le classement des existants */
    const updatedExisting = bigBoardPlayers.map((p) => ({
      ...p,
      classement: p.classement + 1
    }));
    const newPlayer = { ...player, classement: 1 };
    setBigBoardPlayers([...updatedExisting, newPlayer]);
    setPlayers(players.filter((p) => p.id !== player.id));
    setVisibleThreshold((prev) => prev + 1);
  };

  const removePlayerFromBigBoard = (player) => {
    const remaining = bigBoardPlayers.filter((p) => p.id !== player.id);
    const reIndexed = remaining.map((p, idx) => ({
      ...p,
      classement: remaining.length - idx
    }));
    setBigBoardPlayers(reIndexed);
    setPlayers([...players, player]);
    setVisibleThreshold((prev) => Math.max(1, prev - 1));
  };

  const movePlayerInBigBoard = (dragIndex, hoverIndex) => {
    const reordered = [...bigBoardPlayers];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(hoverIndex, 0, moved);

    const updated = reordered.map((p, idx) => ({
      ...p,
      classement: reordered.length - idx
    }));
    setBigBoardPlayers(updated);
  };

  const DraggableBigBoardPlayer = ({ player, index }) => {
    const ref = useRef(null);
    const [, drag] = useDrag({
      type: 'bigboard-player',
      item: { index }
    });
    const [, drop] = useDrop({
      accept: 'bigboard-player',
      hover: (item) => {
        if (item.index !== index) {
          movePlayerInBigBoard(item.index, index);
          item.index = index;
        }
      }
    });
    drag(drop(ref));

    return (
      <Box
        ref={ref}
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 1,
          p: 0.5,
          borderRadius: 1,
          cursor: 'move',
          backgroundColor: 'background.paper',
          '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
        }}
      >
        <Typography sx={{ width: 28, mr: 1 }}>{player.classement}</Typography>
        <Avatar
          src={`${mediaUrl}/photos/${player.id}.png`}
          alt={player.nom}
          sx={{ width: 32, height: 32, mr: 1 }}
        />
        <Typography sx={{ flexGrow: 1 }}>{player.nom}</Typography>
        {!isVisible(player, visibleThreshold) && (
          <VisibilityOff fontSize="small" sx={{ mr: 1 }} />
        )}
        <IconButton
          size="small"
          onClick={() => removePlayerFromBigBoard(player)}
        >
          <DeleteIcon color="error" fontSize="small" />
        </IconButton>
      </Box>
    );
  };

  const BigBoardDropZone = () => {
    const [{ isOver }, dropRef] = useDrop({
      accept: 'player',
      drop: (player) => movePlayerToBigBoard(player),
      collect: (monitor) => ({ isOver: monitor.isOver() })
    });

    /* Affiche en ordre inverse pour avoir #1 en bas */
    const display = [...bigBoardPlayers].reverse();

    return (
      <Paper
        ref={dropRef}
        sx={{
          p: 2,
          minHeight: 400,
          backgroundColor: isOver ? 'rgba(255,152,0,0.1)' : '#f5f5f5'
        }}
      >
        <Typography variant="h6" gutterBottom>
          {t('bigBoard.ranking')}
        </Typography>
        {display.map((p, i) => (
          <DraggableBigBoardPlayer
            key={p.id}
            player={p}
            index={bigBoardPlayers.length - 1 - i}
          />
        ))}
        {bigBoardPlayers.length === 0 && (
          <Typography sx={{ textAlign: 'center', mt: 2, color: 'text.secondary' }}>
            {t('bigBoard.dragPlayersHere')}
          </Typography>
        )}
      </Paper>
    );
  };

  /* -------------------------------------------------- */
  /*            Enregistrement du BigBoard              */
  /* -------------------------------------------------- */
  const handleSaveBigBoard = async () => {
    if (!bigBoardName.trim()) {
      alert(t('bigBoard.alertEnterName'));
      return;
    }
    if (!bigBoardPlayers.length) {
      alert(t('bigBoard.alertAddPlayers'));
      return;
    }
    if (!selectedTags.length) {
      alert(t('bigBoard.alertSelectTags'));
      return;
    }

    try {
      const userId = userData?.id;
      if (!userId) {
        alert(t('bigBoard.alertUserIdError'));
        return;
      }

      /* Construire payload players : ordre inverse (= classement croissant) */
      const payloadPlayers = [...bigBoardPlayers]
        .reverse()
        .map((p) => ({
          id: p.id,
          visible: isVisible(p, visibleThreshold) ? '' : 'non'
        }));

      await api.post('/api/bigboard', {
        nom: bigBoardName,
        tags: selectedTags,
        players: payloadPlayers,
        user_id: userId,
        type: playerType
      });

      alert(t('bigBoard.alertSaveSuccess'));
      /* Reset */
      setBigBoardName('');
      setSelectedTags([]);
      setSelectedRankingNameOption('');
      setBigBoardPlayers([]);
      setVisibleThreshold(5);
    } catch (err) {
      console.error('Error al guardar el Ranking.', err);
      alert(t('bigBoard.alertSaveError'));
    }
  };

  /* -------------------------------------------------- */
  /*              COMPOSANTS INTERNES FILTRES           */
  /* -------------------------------------------------- */

  const FilterCard = ({ label, filter, onColorClick, icon }) => (
    <Card
      sx={{
        minWidth: 160,
        height: 90,
        border: '1px solid',
        borderColor: filter ? '#1976d2' : '#e0e0e0',
        backgroundColor: filter ? 'rgba(25, 118, 210, 0.04)' : 'white',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          borderColor: '#1976d2',
          boxShadow: '0 2px 8px rgba(25, 118, 210, 0.15)'
        }
      }}
    >
      <CardContent sx={{
        pt: 0,
        px: 1.5,
        pb: 1.5,
        '&:last-child': { pb: 1.5 },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
      }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {icon}
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: filter ? '#1976d2' : '#666',
                fontSize: '1.1rem'
              }}
            >
              {label}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.75 }}>
            <Button
              variant={filter === 'red' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => onColorClick('red')}
              sx={{
                minWidth: 55,
                height: 38,
                fontSize: '0.85rem',
                backgroundColor: filter === 'red' ? '#f44336' : 'transparent',
                borderColor: '#f44336',
                color: filter === 'red' ? 'white' : '#f44336',
                '&:hover': {
                  backgroundColor: filter === 'red' ? '#d32f2f' : 'rgba(244, 67, 54, 0.08)',
                  borderColor: '#f44336',
                }
              }}
            >
              0-1
            </Button>
            <Button
              variant={filter === 'green' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => onColorClick('green')}
              sx={{
                minWidth: 55,
                height: 38,
                fontSize: '0.85rem',
                backgroundColor: filter === 'green' ? '#4caf50' : 'transparent',
                borderColor: '#4caf50',
                color: filter === 'green' ? 'white' : '#4caf50',
                '&:hover': {
                  backgroundColor: filter === 'green' ? '#388e3c' : 'rgba(76, 175, 80, 0.08)',
                  borderColor: '#4caf50',
                }
              }}
            >
              1+
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const PlayerTypeCard = () => (
    <Card
      sx={{
        minWidth: 220,
        height: 90,
        border: '1px solid',
        borderColor: (playerTypeFilter === 1 || playerTypeFilter === 2) ? '#1976d2' : '#e0e0e0',
        backgroundColor: (playerTypeFilter === 1 || playerTypeFilter === 2) ? 'rgba(25, 118, 210, 0.04)' : 'white',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          borderColor: '#1976d2',
          boxShadow: '0 2px 8px rgba(25, 118, 210, 0.15)'
        }
      }}
    >
      <CardContent sx={{
        pt: 0,
        px: 1.5,
        pb: 1.5,
        '&:last-child': { pb: 1.5 },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
      }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: (playerTypeFilter === 1 || playerTypeFilter === 2) ? '#1976d2' : '#666',
                fontSize: '1.1rem'
              }}
            >
              {t('bigBoardFilters.playerType')}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 0.75 }}>
            <ToggleButton
              value="pro"
              selected={playerTypeFilter === 2}
              onChange={() => setPlayerTypeFilter(playerTypeFilter === 2 ? 0 : 2)}
              sx={{
                border: '1px solid',
                borderColor: playerTypeFilter === 2 ? '#ff5722' : '#e0e0e0',
                backgroundColor: playerTypeFilter === 2 ? 'rgba(255, 87, 34, 0.08)' : 'transparent',
                '&:hover': {
                  backgroundColor: playerTypeFilter === 2 ? 'rgba(255, 87, 34, 0.15)' : 'rgba(0, 0, 0, 0.04)',
                },
                '&.Mui-selected': {
                  backgroundColor: 'rgba(255, 87, 34, 0.08)',
                  borderColor: '#ff5722',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 87, 34, 0.15)',
                  }
                },
                px: 1.5,
                py: 0.75,
                minWidth: 70,
                height: 38,
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                <Person sx={{
                  fontSize: 16,
                  color: playerTypeFilter === 2 ? '#ff5722' : '#757575'
                }} />
                <Typography variant="caption" sx={{
                  color: playerTypeFilter === 2 ? '#ff5722' : '#757575',
                  fontWeight: playerTypeFilter === 2 ? 600 : 400,
                  fontSize: '0.7rem'
                }}>
                  {t('bigBoardFilters.pro')}
                </Typography>
              </Box>
            </ToggleButton>

            <ToggleButton
              value="cantera"
              selected={playerTypeFilter === 1}
              onChange={() => setPlayerTypeFilter(playerTypeFilter === 1 ? 0 : 1)}
              sx={{
                border: '1px solid',
                borderColor: playerTypeFilter === 1 ? '#2196f3' : '#e0e0e0',
                backgroundColor: playerTypeFilter === 1 ? 'rgba(33, 150, 243, 0.08)' : 'transparent',
                '&:hover': {
                  backgroundColor: playerTypeFilter === 1 ? 'rgba(33, 150, 243, 0.15)' : 'rgba(0, 0, 0, 0.04)',
                },
                '&.Mui-selected': {
                  backgroundColor: 'rgba(33, 150, 243, 0.08)',
                  borderColor: '#2196f3',
                  '&:hover': {
                    backgroundColor: 'rgba(33, 150, 243, 0.15)',
                  }
                },
                px: 1.5,
                py: 0.75,
                minWidth: 70,
                height: 38,
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                <School sx={{
                  fontSize: 16,
                  color: playerTypeFilter === 1 ? '#2196f3' : '#757575'
                }} />
                <Typography variant="caption" sx={{
                  color: playerTypeFilter === 1 ? '#2196f3' : '#757575',
                  fontWeight: playerTypeFilter === 1 ? 600 : 400,
                  fontSize: '0.7rem'
                }}>
                  {t('bigBoardFilters.cantera')}
                </Typography>
              </Box>
            </ToggleButton>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  /* -------------------------------------------------- */
  /*                   RENDER UI                        */
  /* -------------------------------------------------- */
  return (
    <DndProvider backend={HTML5Backend}>
      <Box sx={{ p: 2 }}>
        

        {/* -------- Curseur de visibilité -------- */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            {t('bigBoard.visibility')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={8} md={6}>
              <Slider
                value={visibleThreshold}
                min={1}
                max={Math.max(1, bigBoardPlayers.length || 5)}
                step={1}
                marks
                valueLabelDisplay="auto"
                onChange={(_, v) => setVisibleThreshold(v)}
                sx={{ color: '#ff9800' }}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <TextField
                label={t('bigBoard.topVisible')}
                type="number"
                inputProps={{
                  min: 1,
                  max: bigBoardPlayers.length || 1
                }}
                value={visibleThreshold}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  setVisibleThreshold(Number.isNaN(v) ? 1 : v);
                }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* -------- Section Filtres -------- */}
        <Paper sx={{ p: 3, mb: 4, backgroundColor: '#fafafa' }} elevation={1}>
         

          {/* Première ligne : Filtres principaux */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {userRole !== 'entrenador' && (
              <Grid item xs={12} md={3}>
                <PlayerTypeCard />
              </Grid>
            )}
            <Grid item xs={12} md={userRole === 'entrenador' ? 12 : 9}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <FilterCard
                  label={t('bigBoardFilters.informe')}
                  filter={informeFilter}
                  onColorClick={handleInformeClick}
                  icon={<InformeIcon fontSize="small" />}
                />
                <FilterCard
                  label={t('bigBoardFilters.scout')}
                  filter={scoutFilter}
                  onColorClick={handleScoutClick}
                  icon={<ScoutIcon fontSize="small" />}
                />
                <Card
                  sx={{
                    minWidth: 160,
                    height: 90,
                    border: '1px solid',
                    borderColor: verdictFilter && verdictFilter.length > 0 ? '#1976d2' : '#e0e0e0',
                    backgroundColor: verdictFilter && verdictFilter.length > 0 ? 'rgba(25, 118, 210, 0.04)' : 'white',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      borderColor: '#1976d2',
                      boxShadow: '0 2px 8px rgba(25, 118, 210, 0.15)'
                    }
                  }}
                >
                  <CardContent sx={{
                    pt: 0.5,
                    px: 1.5,
                    pb: 1,
                    '&:last-child': { pb: 1 },
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}>
                    <FormControl size="small" fullWidth variant="standard">
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: verdictFilter && verdictFilter.length > 0 ? '#1976d2' : '#666',
                          fontSize: '1.1rem',
                          textAlign: 'center',
                          mb: 1
                        }}
                      >
                        {t('bigBoardFilters.verdict')}
                      </Typography>
                      <Select
                        multiple
                        value={verdictFilter || []}
                        onChange={(e) => setVerdictFilter(e.target.value)}
                        variant="standard"
                        sx={{
                          fontSize: '0.85rem',
                          '.MuiSelect-select': {
                            padding: '4px 0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: '32px'
                          }
                        }}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', gap: 0.3, alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
                            {selected.length === 0 ? (
                              <Typography variant="caption" sx={{ color: '#999' }}>{t('bigBoardFilters.verdictSelect')}</Typography>
                            ) : selected.length > 2 ? (
                              <Chip
                                label={t('bigBoardFilters.verdictSelected', { count: selected.length })}
                                size="small"
                                sx={{ height: 20, fontSize: '0.75rem' }}
                              />
                            ) : (
                              selected.map((verdict) => {
                                const option = verdictOptions.find(v => v.value === verdict);
                                return (
                                  <Box
                                    key={verdict}
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: '50%',
                                      backgroundColor: option?.color
                                    }}
                                  />
                                );
                              })
                            )}
                          </Box>
                        )}
                      >
                        {verdictOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            <Checkbox checked={(verdictFilter || []).indexOf(option.value) > -1} size="small" />
                            <ListItemText primary={option.label} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </CardContent>
                </Card>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ mb: 3 }} />

          {/* Deuxième ligne : Filtres texte et sélection */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label={t('bigBoardFilters.searchNameTeam')}
                variant="outlined"
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label={t('bigBoardFilters.birthYear')}
                variant="outlined"
                value={yearFilter}
                onChange={(e) => setYearFilter(e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('bigBoardFilters.position')}</InputLabel>
                <Select
                  multiple
                  value={positionFilter}
                  onChange={(e) => setPositionFilter(e.target.value)}
                  renderValue={(selected) => {
                    return selected.map(key => {
                      const pos = positionsWithKeys.find(p => p.key === key);
                      return pos ? pos.label : key;
                    }).join(', ');
                  }}
                >
                  {positionsWithKeys.map((position) => (
                    <MenuItem key={position.key} value={position.key}>
                      <Checkbox checked={positionFilter.indexOf(position.key) > -1} />
                      <ListItemText primary={position.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('bigBoardFilters.groups')}</InputLabel>
                <Select
                  multiple
                  value={groupFilter}
                  onChange={(e) => setGroupFilter(e.target.value)}
                  renderValue={(selected) => (
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.5,
                        maxWidth: '100%',
                        overflow: 'hidden',
                      }}
                    >
                      {selected.length <= 2 ? (
                        selected.map((group) => (
                          <Chip
                            key={group}
                            label={group}
                            size="small"
                            sx={{
                              backgroundColor: groupColors[group] || 'grey',
                              color: getContrastColor(groupColors[group]),
                              maxWidth: '120px',
                              '& .MuiChip-label': {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              },
                            }}
                          />
                        ))
                      ) : (
                        <Typography variant="body2">{t('bigBoardFilters.groupsCount', { count: selected.length })}</Typography>
                      )}
                    </Box>
                  )}
                >
                  {groups.map((group) => (
                    <MenuItem key={group.libelle} value={group.libelle}>
                      <Checkbox checked={groupFilter.indexOf(group.libelle) > -1} />
                      <ListItemText primary={group.libelle} />
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          backgroundColor: group.code_couleur || 'grey',
                          borderRadius: '50%',
                          ml: 1,
                        }}
                      />
                    </MenuItem>
                  ))}
                  <MenuItem value="Sin Grupos">
                    <Checkbox checked={groupFilter.indexOf('Sin Grupos') > -1} />
                    <ListItemText primary={t('bigBoardFilters.noGroups')} />
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Troisième ligne : Sliders */}
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  {t('bigBoardFilters.heightRange', { min: heightFilter[0], max: heightFilter[1] })}
                </Typography>
                <Slider
                  value={heightFilter}
                  onChange={(e, newValue) => setHeightFilter(newValue)}
                  valueLabelDisplay="auto"
                  min={100}
                  max={230}
                  sx={{ color: '#1976d2' }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  {t('bigBoardFilters.ageRange', { min: ageFilter[0], max: ageFilter[1] })}
                </Typography>
                <Slider
                  value={ageFilter}
                  onChange={(e, newValue) => setAgeFilter(newValue)}
                  valueLabelDisplay="auto"
                  min={8}
                  max={42}
                  sx={{ color: '#1976d2' }}
                />
              </Box>
            </Grid>
          </Grid>
        </Paper>


           

        {/* Nom du ranking + tags */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="rank-name">{t('bigBoard.rankingName')}</InputLabel>
              <Select
                labelId="rank-name"
                value={selectedRankingNameOption}
                onChange={(e) => {
                  const v = e.target.value;
                  setSelectedRankingNameOption(v);
                  setBigBoardName(v !== 'Otro' ? v : '');
                }}
              >
                <MenuItem value="">{t('bigBoard.selectOption')}</MenuItem>
                <MenuItem value="Bases">{t('bigBoard.bases')}</MenuItem>
                <MenuItem value="Escoltas">{t('bigBoard.escoltas')}</MenuItem>
                <MenuItem value="Aleros">{t('bigBoard.aleros')}</MenuItem>
                <MenuItem value="Ala-Pívots">{t('bigBoard.alaPivots')}</MenuItem>
                <MenuItem value="Pívots">{t('bigBoard.pivots')}</MenuItem>
                <MenuItem value="Otro">{t('bigBoard.other')}</MenuItem>
              </Select>
            </FormControl>
            {selectedRankingNameOption === 'Otro' && (
              <TextField
                sx={{ mt: 2 }}
                fullWidth
                label={t('bigBoard.otherNamePlaceholder')}
                value={bigBoardName}
                onChange={(e) => setBigBoardName(e.target.value)}
              />
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="tags-label">{t('bigBoard.selectTags')}</InputLabel>
              <Select
                labelId="tags-label"
                multiple
                value={selectedTags}
                onChange={(e) => setSelectedTags(e.target.value)}
                renderValue={(sel) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {sel.map((id) => {
                      const tag = tags.find((tg) => tg.id === id);
                      return (
                        <Chip
                          key={id}
                          label={tag?.libelle}
                          sx={{ backgroundColor: tag?.code_couleur, color: 'white' }}
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {tags.map((tag) => (
                  <MenuItem key={tag.id} value={tag.id}>
                    <Checkbox checked={selectedTags.includes(tag.id)} />
                    <Chip
                      label={tag.libelle}
                      sx={{
                        backgroundColor: tag.code_couleur,
                        color: 'white',
                        mr: 1
                      }}
                    />
                    <ListItemText primary={tag.libelle} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Button
          variant="contained"
          sx={{ backgroundColor: '#ff9800', color: 'white', mb: 4 }}
          onClick={handleSaveBigBoard}
        >
          {t('bigBoard.saveRanking')}
        </Button>

        {/* -------- Tableau joueurs + BigBoard -------- */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
              <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        backgroundColor: '#F25F29',
                        color: 'white',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        width: '50px',
                        padding: '8px 4px'
                      }}
                    >
                      <Checkbox
                        disabled
                        sx={{ color: 'white' }}
                      />
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#F25F29', color: 'white', fontWeight: 'bold', textAlign: 'center', width: '120px', padding: '8px 4px' }}>
                      <Box sx={{
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        borderRadius: '4px',
                        padding: '2px 6px',
                        border: '1px solid rgba(255,255,255,0.3)',
                        display: 'inline-block'
                      }}>
                        <span style={{ fontSize: '12px', fontWeight: 'bold' }}>{filteredPlayers.length}</span>
                        <span style={{ fontSize: '9px', opacity: 0.8, marginLeft: '2px' }}>{t('bigBoard.table.players')}</span>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#F25F29', color: 'white', fontWeight: 'bold', textAlign: 'left', paddingLeft: '8px', width: '120px', padding: '8px 4px' }}>
                      {t('bigBoard.table.name')}
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#F25F29', color: 'white', fontWeight: 'bold', textAlign: 'left', paddingLeft: '8px', width: '60px', padding: '8px 4px' }}>
                      {t('bigBoard.table.form')}
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#F25F29', color: 'white', fontWeight: 'bold', textAlign: 'left', paddingLeft: '8px', width: '80px', padding: '8px 4px' }}>
                      {t('bigBoard.table.position')}
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#F25F29', color: 'white', fontWeight: 'bold', textAlign: 'left', paddingLeft: '8px', width: '100px', padding: '8px 4px' }}>
                      {t('bigBoard.table.year')}
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#F25F29', color: 'white', fontWeight: 'bold', textAlign: 'left', paddingLeft: '8px', width: '60px', padding: '8px 4px' }}>
                      {t('bigBoard.table.height')}
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#F25F29', color: 'white', fontWeight: 'bold', textAlign: 'center', width: '100px', padding: '8px 4px' }}>
                      {t('bigBoard.table.nationality')}
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#F25F29', color: 'white', fontWeight: 'bold', textAlign: 'center', width: '120px', padding: '8px 4px' }}>
                      {t('bigBoard.table.team')}
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#F25F29', color: 'white', fontWeight: 'bold', textAlign: 'left', paddingLeft: '8px', width: '80px', padding: '8px 4px' }}>
                      {t('bigBoard.table.groups')}
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#F25F29', color: 'white', fontWeight: 'bold', textAlign: 'center', width: '80px', padding: '8px 4px' }}>
                      {t('bigBoard.table.informeScout')}
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#F25F29', color: 'white', fontWeight: 'bold', textAlign: 'center', width: '140px', padding: '8px 4px' }}>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getCurrentPagePlayers().map((p) => (
                    <PlayerRow key={p.id} player={p} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredPlayers.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, np) => setPage(np)}
              rowsPerPageOptions={[rowsPerPage]}
              labelDisplayedRows={({ from, to, count }) =>
                t('bigBoard.pagination.displayedRows', { from, to, count })
              }
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <BigBoardDropZone />
          </Grid>
        </Grid>
      </Box>
    </DndProvider>
  );
};

export default BigBoard;
